import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useField } from 'formik';
import NumberFormat from 'react-number-format';
import Select from 'react-select';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { setComboSetValueDictionary } from '@redux/modules/formGeneration';

import {
  selectStatusByName,
  selectLookupNames,
  selectAllComboBoxOptions,
  selectComboBoxOptions,
  selectYNOptions, // determines options to show for YN values depending on isRequired
  selectRequiredStatusByFieldName, // retrieves required field objects from the validationResults
} from '@redux/selectors/FormSelector';

import {
  selectListingFormData,
  selectAgentFormData,
  selectOfficeFormData,
} from '@redux/selectors/FormDataSelector';

import ComboEnforcer from '@components/form/formHelpers/ParentChildEnforcer';
// import { isNull } from 'lodash';
import TextareaAutosize from 'react-textarea-autosize';

/*
ComboBox Attributes
- multiselect
- dataType: string
- inputType: ComboBox
- fieldWidth
- fieldLabel
- isRequired
- showHelpIcon
- helpText
- comboBoxValue
- comboBoxLabel
- comboBoxParentFieldName
- comboBoxParentLinkAttribute
- comboBoxChildLinkAttribute

TextInput/MaskedTextInput Attributes
- dataType: string, integer, numeric, date, datetime, time
- inputType: TextInput / MaskedTextInput
- fieldWidth
- fieldHeight (multi row)
- fieldLabel
- isRequired
- showHelpIcon
- helpText
- isHidden
- sortOrder
*/

const FormikInputGroup = ({
  comboClear, // reset flag for combos
  setComboClear, // set reset flag for combos
  datatype, // = 'string',
  // disabled = false, // Removed in favor of IsDisabled flag
  fieldLabel,
  FieldName,
  helpText,
  inputType, // = 'TextInput',
  IsVisible,
  IsDisabled,
  multiselect = false,
  // PageName, // section name e.g. Remarks, General, etc
  Precision,
  maxLength,
  showHelpIcon = false,
  validateFigField,
  comboBubbleWrap = {
    AddComboMutator: () => { },
    setNewComboValue: () => { },
    setNewComboOptions: () => { },
  },
}) => {
  const dispatch = useDispatch();

  const [field, meta, helpers] = useField(FieldName);
  const [localFieldValue, setLocalFieldValue] = useState(field?.value);
  const [checkOpt, setCheckOpt] = useState('');
  const [comboValue, setComboValue] = useState('');
  const [characterCount, setCharacterCount] = useState(0);

  const listingFormData = useSelector(selectListingFormData);
  const agentFormData = useSelector(selectAgentFormData);
  const officeFormData = useSelector(selectOfficeFormData);
  const lookUpNames = useSelector(selectLookupNames);
  const allComboBoxOptions = useSelector(selectAllComboBoxOptions);
  const comboBoxOptions = useSelector(state => selectComboBoxOptions(state, FieldName, inputType === 'ComboBox'));
  const comboIsLoading = useSelector(state => state.formGeneration.comboIsLoading); // combo loading status
  const requiredFields = useSelector(state => selectRequiredStatusByFieldName(state, FieldName)); // returns isRequired prop per fieldname in the form
  const staticOptions = useSelector(state => selectYNOptions(state, FieldName));
  const status = useSelector(state => selectStatusByName(state, FieldName));
  const fieldIsVisible = IsVisible;
  const fieldIsDisabled = IsDisabled ?? false;
  const controlRef = useRef(null);

  // string array for combo placeholder to force display of TAP values in form
  const validationBubble = fieldValue => {
    helpers.setTouched(true);
    validateFigField(FieldName, fieldValue);
  };

  const comboChange = opt => {
    let blurValue = '';
    if (opt) {
      if (multiselect) {
        blurValue = opt?.map(option => option.value).join(',') ?? '';
      } else if (datatype !== 'bit') {
        blurValue = opt.value ?? '';
        setCheckOpt(opt);
      } else {
        blurValue = opt.value ?? '';
      }
    }
    setComboValue(opt ?? '');
    if (field?.value === blurValue) return;
    helpers.setValue(blurValue);
    validationBubble(blurValue);
  };

  // Combo values can be updated externally via redux (see usage of setComboSetValueDictionary and comboSetValueDictionary).
  useEffect(() => {
    if (inputType === 'ComboBox') {
      comboChange(comboValue);
    }
  }, [comboValue]);

  // sets combo value to empty to remove any values displaying
  useEffect(() => {
    if (comboClear && inputType === 'ComboBox') {
      setComboValue('');
      setComboClear(false);
    }
  }, [comboClear]);

  useEffect(() => {
    if (inputType === 'ComboBox') {
      dispatch(setComboSetValueDictionary({ [FieldName]: setComboValue }));
    }
  }, []);

  /*
    This is still here to update the comboBox, date time and bit fields.
    Because they read their values from state, this will need to potentially
    be moved at another time.
  */
  const updateFIGValues = updatedData => {
    if (updatedData) {
      Object.entries(updatedData).map(([key, value]) => {
        if (field.name !== key) return null;

        switch (inputType) {
          case 'TextInput':
            if (datatype === 'bit') {
              let bitValue;
              if (value != null && value !== '') {
                if (typeof value === 'string') {
                  bitValue = value === 'true' ? 1 : 0;
                } else if (typeof value === 'boolean') {
                  bitValue = value === true ? 1 : 0;
                } else if (typeof value === 'number') {
                  bitValue = value;
                }
              }
              if (bitValue != null) {
                const option = {
                  label: bitValue ? 'Yes' : 'No',
                  value: bitValue,
                };
                comboChange(option);
              }
            }
            break;

          case 'ComboBox':
            if (comboBoxOptions?.length) {
              const multiSelectedOptions = [];
              let singleSelectOption = null;
              comboBoxOptions.forEach(opt => {
                // checking that field names and combo options match popfield value to display
                if (value) {
                  if (multiselect) {
                    value.split(',').map(val => {
                      if (val === opt.value) {
                        multiSelectedOptions.push(opt);
                      }
                      return null;
                    });
                  }
                  if (value === opt.value) {
                    if (!multiselect) {
                      singleSelectOption = opt;
                    }
                  }
                }
              });
              if (multiselect) {
                comboChange(multiSelectedOptions);
              }
              if (!multiselect) {
                comboChange(singleSelectOption);
              }
            }
            break;

          default: break;
        }
        return null;
      });
    }
  };

  useEffect(() => {
    if (listingFormData && (!lookUpNames?.lookupNames?.length || allComboBoxOptions?.length)) {
      updateFIGValues(listingFormData);
    }
  }, [listingFormData, allComboBoxOptions]);

  useEffect(() => {
    if (agentFormData && (!lookUpNames?.lookupNames?.length || allComboBoxOptions?.length)) {
      updateFIGValues(agentFormData);
    }
  }, [agentFormData, allComboBoxOptions]);

  useEffect(() => {
    if (officeFormData && (!lookUpNames?.lookupNames?.length || allComboBoxOptions?.length)) {
      updateFIGValues(officeFormData);
    }
  }, [officeFormData, allComboBoxOptions]);

  const sameValue = (oldValue, newValue) => {
    if (oldValue === newValue) return true;
    if (oldValue?.toString() === newValue?.toString()) return true;
    // if (!oldValue && !newValue) return true;
    return false;
  };

  const handleBlur = e => {
    let validationValue = e.target.value;
    const isNumber = datatype === 'numeric' || datatype === 'decimal' || datatype === 'integer';
    if (isNumber && typeof (validationValue) === 'string') {
      validationValue = validationValue.replace(/,/g, '');
    }
    if (sameValue(field?.value, validationValue)) return;
    helpers.setValue(validationValue);
    validationBubble(validationValue);
  };

  const handleChange = ({ target }) => {
    setLocalFieldValue(target.value);
    setCharacterCount(target.value.length);
  };

  const handleFocus = () => {
    controlRef.current.focus();
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  useEffect(() => {
    if (inputType !== 'TextInput') return;
    if (!(datatype === 'string' || datatype === 'integer' || datatype === 'decimal' || datatype === 'numeric')) return;
    if (field?.value == null || field.value === localFieldValue) return;
    setLocalFieldValue(field.value);
  }, [field?.value]);

  // display an asterisk beside the field label if a field is required
  const isRequiredSymbol = requiredFields ? <span className="invalid-feedback">&nbsp;*</span> : '';

  const classNames = 'form-group';
  // if (!fieldIsVisible) classNames = 'd-none'; // this almost works, but isVisible is not being returned properly.
  if (!fieldIsVisible) return null;

  return (
    <div className={classNames} id={fieldLabel}>
      <label
        className="form-label"
        htmlFor={FieldName}
      >
        {fieldLabel}
        {isRequiredSymbol}
      </label>
      {
        showHelpIcon && (
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id={`tooltip-${FieldName}-input-help`} style={{ margin: 0 }}>{helpText}</Tooltip>}
          >
            <i className="fal fa-question-circle float-right" />
          </OverlayTrigger>
        )
      }
      {
        inputType === 'TextInput' && (
          <>
            {
              (datatype === 'string' && maxLength < 200)
              && (
                <>
                  <div className="input-group">
                    {fieldLabel.includes('URL') && (<div className="input-group-prepend"><span className="input-group-text"><i className="far fa-link" /></span></div>)}
                    <input
                      type="text"
                      id={FieldName}
                      name={FieldName}
                      className="form-control text-input"
                      value={localFieldValue ?? ''} // Do not remove, otherwise it throws an uncontrolled component error
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onKeyPress={handleKeyPress}
                      maxLength={maxLength}
                      autoComplete="off"
                      disabled={fieldIsDisabled}
                    />
                    {(!fieldIsDisabled && maxLength > 50) && (
                      <div className="input-group-append">
                        <div className="input-group-text">
                          {maxLength > 0 && !fieldIsDisabled ? `${characterCount} / ${maxLength}` : null}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )
            }
            {
              (datatype === 'string' && maxLength >= 200)
              && (
                <>
                  <div className="input-group">
                    {fieldLabel.includes('URL') && (<div className="input-group-prepend"><span className="input-group-text"><i className="far fa-link" /></span></div>)}
                    <TextareaAutosize
                      type="text"
                      id={FieldName}
                      name={FieldName}
                      className="form-control text-input"
                      value={localFieldValue ?? ''} // Do not remove, otherwise it throws an uncontrolled component error
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onKeyPress={handleKeyPress}
                      maxLength={maxLength}
                      autoComplete="off"
                      maxRows={4}
                      disabled={fieldIsDisabled}
                    />
                    {(!fieldIsDisabled && maxLength > 50) && (
                      <div className="input-group-append">
                        <div className="input-group-text">
                          {maxLength > 0 && !fieldIsDisabled ? `${characterCount} / ${maxLength}` : null}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )
            }
            {
              datatype === 'integer'
              && (
                <NumberFormat
                  id={FieldName}
                  name={FieldName}
                  className="form-control"
                  value={localFieldValue}
                  decimalScale={0}
                  displayType="input"
                  inputMode="numeric"
                  isNumericString
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onKeyPress={handleKeyPress}
                  thousandSeparator={false}
                  autoComplete="off"
                  disabled={fieldIsDisabled}
                />
              )
            }
            {
              datatype === 'bit'
              && (
                <Select
                  ref={controlRef}
                  inputId={FieldName}
                  name={FieldName}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  isClearable
                  onChange={comboChange}
                  onFocus={handleFocus}
                  options={staticOptions}
                  placeholder="Choose one"
                  value={comboValue}
                  autoComplete="off"
                  isDisabled={fieldIsDisabled}
                />
              )
            }
            {
              datatype === 'numeric'
              && (
                <div className="input-group">
                  {(fieldLabel.includes('Price') || fieldLabel.includes('Tax') || fieldLabel.includes('Rent') || fieldLabel.includes('Fee')) && (<div className="input-group-prepend"><span className="input-group-text">$</span></div>)}
                  <NumberFormat
                    type="text"
                    id={FieldName}
                    name={FieldName}
                    className="form-control text-input"
                    value={localFieldValue}
                    displayType="input"
                    decimalScale={Precision}
                    thousandSeparator
                    isNumericString
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onKeyPress={handleKeyPress}
                    autoComplete="off"
                    disabled={fieldIsDisabled}
                  />
                  {(fieldLabel.includes('Price') || fieldLabel.includes('Tax') || fieldLabel.includes('Rent') || fieldLabel.includes('Fee')) && (<div className="input-group-prepend"><span className="input-group-text">00</span></div>)}
                </div>
              )
            }
          </>
        )
      }
      {
        inputType === 'ComboBox' && !multiselect
        && (
          <Select
            ref={controlRef}
            inputId={FieldName}
            name={FieldName}
            className="react-select-container"
            classNamePrefix="react-select"
            value={comboValue}
            closeMenuOnSelect={!multiselect}
            isMulti={multiselect}
            isClearable
            isLoading={comboIsLoading}
            onChange={comboChange} // used to select and format values for validation/flexRule
            options={comboBoxOptions}
            onFocus={handleFocus}
            placeholder="Choose one"
            autoComplete="off"
            isDisabled={fieldIsDisabled}
            menuShouldScrollIntoView
            menuPortalTarget={document.querySelector('body')}
            styles={{
              menuPortal: styles => ({ ...styles, zIndex: 9999 }),
            }}
          />
        )
      }
      {
        inputType === 'ComboBox' && multiselect
        && (
          <Select
            ref={controlRef}
            inputId={FieldName}
            name={FieldName}
            className="react-select-container"
            classNamePrefix="react-select"
            value={comboValue}
            closeMenuOnSelect={!multiselect}
            isMulti={multiselect}
            isClearable
            isLoading={comboIsLoading}
            onChange={comboChange} // used to select and format values for validation/flexRule
            options={comboBoxOptions}
            onFocus={handleFocus}
            placeholder="Choose one or more"
            autoComplete="off"
            isDisabled={fieldIsDisabled}
            menuShouldScrollIntoView
            menuPortalTarget={document.querySelector('body')}
            styles={{
              multiValue: (styles, { data }) => ({
                ...styles,
                backgroundColor: data?.label?.endsWith('(disabled)')
                  ? 'hsl(0, 0%, 60%)'
                  : styles.backgroundColor,
              }),
              menuPortal: styles => ({ ...styles, zIndex: 9999 }),
            }}
          />
        )
      }
      {
        meta && status && status.errorMessage ? (
          <div className="invalid-feedback">{status.errorMessage}</div>
        ) : null
      }
      {inputType === 'ComboBox'
        && (
          <ComboEnforcer // transparent component, used to unset incompatible combobox values
            valueBubble={comboBubbleWrap.setNewComboValue}
            opt={checkOpt}
            FieldName={FieldName}
          />
        )}
    </div>
  );
};

FormikInputGroup.propTypes = {
  comboClear: PropTypes.bool,
  setComboClear: PropTypes.func,
  datatype: PropTypes.string,
  // disabled: PropTypes.bool,
  fieldLabel: PropTypes.string,
  FieldName: PropTypes.string,
  helpText: PropTypes.string,
  inputType: PropTypes.oneOf(['ComboBox', 'TextInput']),
  IsVisible: PropTypes.bool,
  IsDisabled: PropTypes.bool,
  multiselect: PropTypes.bool,
  // PageName: PropTypes.string,
  Precision: PropTypes.number,
  maxLength: PropTypes.number,
  showHelpIcon: PropTypes.bool,
  validateFigField: PropTypes.func,
  comboBubbleWrap: PropTypes.shape({
    AddComboMutator: PropTypes.func,
    setNewComboValue: PropTypes.func,
    setNewComboOptions: PropTypes.func,
  }),
};

export default FormikInputGroup;
